import React, { useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import TroniaGrafica from './TroniaGrafica'

// [RGM] Chat TronIA (diseño nuevo aprobado por Rafael — Fases 5-7 del plan
// TronIA). Componente reutilizable: lo usan el panel del tab de Mi Cuenta
// (TroniaPanel) y el widget flotante (TroniaFab).
// Consume los endpoints /api/tronia/* (TroniaController — diseño TronIA
// nuevo, NO es réplica GX; los errores del backend llegan en el campo
// "mensaje"). El chat NO es SSE aún: POST normal y respuesta JSON completa.
// Los identificadores internos nuevos están en INGLÉS (convención AGENTS.md);
// los campos del contrato API (consulta, respuesta, mensaje) quedan en
// español porque son el contrato del backend .NET.
// [RGM] Fase 3 (2026-10-04): con plan Profesional+ la respuesta puede traer
// `grafica` (barras) y se dibuja bajo el texto del asistente (TroniaGrafica).

interface PuntoGrafica {
  etiqueta: string
  valor: number
}

interface GraficaTronia {
  titulo: string
  tipo: string
  datos: PuntoGrafica[]
}

interface Message {
  role: 'usuario' | 'tronia'
  text: string
  time: string
  isError?: boolean
  /** Gráfica adjunta del asistente (Fase 3, plan Profesional+); sin ella, null. */
  grafica?: GraficaTronia | null
  /** [RGM] Fase 5: id de la fila guardada en el historial (null si falló). */
  consultaId?: number | null
  /** [RGM] Fase 5: true solo con plan Empresarial — habilita "Enviar por email". */
  emailDisponible?: boolean
}

/** Consulta del historial (GET /tronia/historial, Fase 4). Contrato del
 *  backend: fecha (UTC ISO-8601), pregunta, respuesta y tokens consumidos. */
interface ConsultaHistorial {
  fecha: string
  pregunta: string
  respuesta: string
  tokens: number
}

interface TroniaChatProps {
  enabled?: boolean
  compact?: boolean // variante del widget flotante (sin contenedor de card)
  mode?: 'chat' | 'public' // 'public' = Tronia en el Login (sin sesión, /api/tronia/public)
}

const TroniaChat: React.FC<TroniaChatProps> = ({ enabled = true, compact = false, mode = 'chat' }) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [text, setText] = useState('')
  const [isThinking, setIsThinking] = useState(false)
  // [RGM] Fase 4 (2026-10-04): historial de consultas — se carga al abrir el
  // panel (GET /tronia/historial). El backend acota: usuario → las suyas,
  // admin de la empresa → todas las de su empresa; superadmin → vacío.
  const [historyOpen, setHistoryOpen] = useState(false)
  const [history, setHistory] = useState<ConsultaHistorial[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState('')
  // [RGM] 2026-10-04/05 (pedido Rafael): el historial es SOLO del plan
  // Empresarial (igual que el email) — llega en la respuesta del chat y el
  // botón permanece oculto hasta la primera respuesta que lo habilite.
  const [historialDisponible, setHistorialDisponible] = useState(false)
  const areaRef = useRef<HTMLDivElement>(null)

  // Autoscroll al final al agregar mensajes. Guard defensivo: jsdom (tests)
  // no implementa scrollTo en los elementos — sin el guard, el render de App
  // en vitest crashea (TypeError) y el CI quedaba colgado en el step de tests.
  useEffect(() => {
    if (areaRef.current?.scrollTo) areaRef.current.scrollTo({ top: areaRef.current.scrollHeight })
  }, [messages, isThinking])

  const getCurrentTime = () =>
    new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })

  /** Abre/cierra el historial. Al abrir recarga la lista (sin caché: app ONLINE). */
  const toggleHistory = async () => {
    if (historyOpen) {
      setHistoryOpen(false)
      return
    }
    setHistoryOpen(true)
    setHistoryLoading(true)
    setHistoryError('')
    try {
      const res = await api.get<ConsultaHistorial[]>('/tronia/historial')
      setHistory(Array.isArray(res) ? res : [])
    } catch (err: any) {
      setHistoryError(err?.message ?? 'No se pudo cargar el historial')
    } finally {
      setHistoryLoading(false)
    }
  }

  /** Fecha corta local: el backend guarda UTC y Postgres puede devolver el ISO
   *  sin zona ("2026-10-04T14:00:00") — se agrega Z para no correr la hora. */
  const formatHistoryDate = (fecha: string) => {
    const iso = /(Z|[+-]\d{2}:?\d{2})$/.test(fecha) ? fecha : `${fecha}Z`
    const date = new Date(iso)
    return isNaN(date.getTime())
      ? fecha
      : date.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  }

  const sendMessage = async () => {
    const consulta = text.trim()
    if (!consulta || isThinking || !enabled) return
    setMessages((m) => [...m, { role: 'usuario', text: consulta, time: getCurrentTime() }])
    setText('')
    setIsThinking(true)
    try {
      // mode 'public' (Login): endpoint sin sesión; nunca muestra datos privados.
      const res = await api.post<{
        respuesta: string
        grafica?: GraficaTronia | null
        consultaId?: number | null
        emailDisponible?: boolean
        historialDisponible?: boolean
      }>(
        mode === 'public' ? '/tronia/public' : '/tronia/chat',
        { consulta }
      )
      // [RGM] 2026-10-04/05 (pedido Rafael): el botón Historial es solo del
      // plan Empresarial — el backend lo expone en cada respuesta.
      setHistorialDisponible(res.historialDisponible === true)
      setMessages((m) => [...m, {
        role: 'tronia',
        text: res.respuesta,
        time: getCurrentTime(),
        // Fase 3: null/ausente en los planes sin gráficas (Inicial/Prueba/superadmin).
        grafica: res.grafica ?? null,
        // Fase 5: la fila guardada y la disponibilidad del email (Empresarial).
        consultaId: res.consultaId ?? null,
        emailDisponible: res.emailDisponible === true,
      }])
    } catch (err: any) {
      // 403/error: api.post lanza Error con el "mensaje" del backend
      setMessages((m) => [...m, { role: 'tronia', text: err?.message ?? 'Error inesperado', time: getCurrentTime(), isError: true }])
    } finally {
      setIsThinking(false)
    }
  }

  // [RGM] Fase 5 (2026-10-04): envío de la respuesta por email al correo de
  // la EMPRESA — solo plan Empresarial (el backend responde 400 con el motivo
  // si el plan no aplica, la consulta no es de la empresa o no hay correo).
  // Estado por índice de mensaje: la lista de mensajes solo crece.
  const [emailEnviando, setEmailEnviando] = useState<number | null>(null)
  const [emailResultado, setEmailResultado] = useState<Record<number, { ok: boolean; texto: string }>>({})

  const enviarPorEmail = async (index: number, consultaId: number) => {
    if (emailEnviando !== null) return
    setEmailEnviando(index)
    // Limpia el resultado anterior de ese mensaje mientras envía.
    setEmailResultado((r) => ({ ...r, [index]: { ok: true, texto: '' } }))
    try {
      const res = await api.post<{ mensaje: string }>(`/tronia/historial/${consultaId}/email`, {})
      setEmailResultado((r) => ({ ...r, [index]: { ok: true, texto: res.mensaje || 'Enviado' } }))
    } catch (err: any) {
      setEmailResultado((r) => ({ ...r, [index]: { ok: false, texto: err?.message ?? 'No se pudo enviar el correo' } }))
    } finally {
      setEmailEnviando(null)
    }
  }

  const inputEnabled = enabled && !isThinking

  return (
    <>
      {/* Fase 4 + regla Rafael 2026-10-04/05: encabezado con el historial SOLO
          del plan Empresarial (`historialDisponible` de la respuesta; en el
          Login público no aplica: no hay empresa ni historial). Antes de la
          primera respuesta el botón queda oculto. Icono solo con tooltip
          "Historial" (como el móvil) y aire extra arriba en el FAB compacto. */}
      {mode !== 'public' && historialDisponible && (
        <>
          <div className={`d-flex justify-content-end align-items-center mb-1${compact ? ' pt-2' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={toggleHistory}
              title="Historial"
              aria-label="Historial"
              aria-expanded={historyOpen}
            >
              <i className="fas fa-history" />
            </button>
          </div>
          {historyOpen && (
            <div
              style={{
                maxHeight: 260,
                overflowY: 'auto',
                background: 'var(--cui-card-bg)',
                border: '1px solid var(--cui-border-color)',
                borderRadius: 8,
                padding: '4px 10px',
                marginBottom: 8,
              }}
            >
              {historyLoading ? (
                <div className="small p-2" style={{ color: 'var(--cui-secondary-color)' }}>Cargando…</div>
              ) : historyError ? (
                <div className="small p-2" style={{ color: 'var(--cui-danger)' }}>{historyError}</div>
              ) : history.length === 0 ? (
                <div className="small p-2" style={{ color: 'var(--cui-secondary-color)' }}>Sin consultas este mes</div>
              ) : (
                history.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '6px 0',
                      borderBottom: i < history.length - 1 ? '1px solid var(--cui-border-color)' : 'none',
                    }}
                  >
                    <div className="small" style={{ color: 'var(--cui-secondary-color)' }}>
                      {formatHistoryDate(c.fecha)}
                    </div>
                    <div className="small fw-semibold">{c.pregunta}</div>
                    <div
                      className="small"
                      style={{
                        color: 'var(--cui-secondary-color)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {c.respuesta}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
      <div className="chat-area" ref={areaRef}>
        {messages.length === 0 && !isThinking ? (
          <div className="chat-vacio">
            <div className="chat-vacio-robot"><i className="fas fa-robot" /></div>
            <div className="chat-vacio-titulo">Inicia una conversación con TronIA</div>
            <div className="chat-vacio-sub">
              {mode === 'public'
                ? 'Pregunte sobre Cybertronica, Siprin, SiprinIA o Tronia. Para información de su empresa inicie sesión.'
                : 'Pregunte sobre gestión de cobranza, recuperación de cartera, o estrategias de cobro.'}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`chat-burbuja ${m.role === 'usuario' ? 'chat-burbuja-usuario' : 'chat-burbuja-tronia'}${m.isError ? ' chat-burbuja-error' : ''}`}>
              <span className="chat-avatar"><i className={`fas ${m.role === 'usuario' ? 'fa-user' : 'fa-robot'}`} /></span>
              <div style={{ minWidth: 0 }}>
                <div className="chat-texto">{m.text}</div>
                {/* Fase 3: tarjeta de barras bajo el texto del asistente. */}
                {m.role === 'tronia' && !m.isError && m.grafica && m.grafica.datos?.length > 0 && (
                  <TroniaGrafica titulo={m.grafica.titulo} datos={m.grafica.datos} />
                )}
                {/* Fase 5: enviar la consulta al correo configurado en la
                    empresa (solo plan Empresarial; el backend lo valida). */}
                {m.role === 'tronia' && !m.isError && m.emailDisponible && m.consultaId != null && (
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => enviarPorEmail(i, m.consultaId as number)}
                      disabled={emailEnviando !== null}
                      aria-label="Enviar por email"
                    >
                      <i className={`fas ${emailEnviando === i ? 'fa-spinner fa-spin' : 'fa-envelope'} me-1`} />
                      {emailEnviando === i ? 'Enviando…' : 'Enviar por email'}
                    </button>
                    {emailResultado[i]?.texto && (
                      <div
                        className="small mt-1"
                        style={{ color: emailResultado[i].ok ? 'var(--cui-success)' : 'var(--cui-danger)' }}
                      >
                        {emailResultado[i].texto}
                      </div>
                    )}
                  </div>
                )}
                <div className="chat-hora">{m.time}</div>
              </div>
            </div>
          ))
        )}
        {isThinking && (
          <div className="chat-burbuja chat-burbuja-tronia">
            <span className="chat-avatar"><i className="fas fa-robot" /></span>
            <div className="chat-texto">⟳ TronIA está pensando...</div>
          </div>
        )}
      </div>
      <div className={compact ? 'tr-chat-input-row' : 'd-flex gap-2 mt-2'}>
        <input
          className={compact ? 'tr-chat-input' : 'form-control'}
          placeholder="Escribe tu consulta..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Enter (sin Shift) envía; preventDefault evita el submit del CForm
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              sendMessage()
            }
          }}
          disabled={!inputEnabled}
        />
        <button
          type="button"
          className={compact ? 'tr-chat-send' : 'btn btn-primary'}
          onClick={sendMessage}
          disabled={!inputEnabled || !text.trim()}
          aria-label="Enviar"
        >
          ➤
        </button>
      </div>
    </>
  )
}

export default TroniaChat
